import type { Components, Theme } from '@mui/material/styles';
import { RADIUS, soft, STATUS_COLORS } from './soft';

export const data: Components<Theme> = {
  MuiTableContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: RADIUS.container,
        backgroundColor: theme.palette.background.paper,
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({ borderColor: theme.palette.divider, paddingTop: 12, paddingBottom: 12 }),
      head: ({ theme }) => ({
        ...theme.typography.overline,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.surface.subtle,
        whiteSpace: 'nowrap',
        paddingTop: 10,
        paddingBottom: 10,
      }),
      sizeSmall: ({ theme }) => ({ paddingInline: theme.spacing(2) }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:last-child > .MuiTableCell-body': { borderBottom: 0 },
        '&.MuiTableRow-hover:hover': { backgroundColor: theme.palette.surface.subtle },
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: ({ theme }) => ({ color: theme.palette.text.secondary, borderTop: 0 }),
    },
  },
  MuiChip: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 6,
        fontWeight: 500,
        border: '1px solid transparent',
        variants: [
          { props: { variant: 'filled', color: 'default' }, style: { backgroundColor: theme.palette.surface.sunken, color: theme.palette.text.primary } },
          { props: { variant: 'outlined', color: 'default' }, style: { borderColor: theme.palette.surface.border } },
          // Every coloured chip is tonal, filled or outlined, so status reads the same on every screen.
          ...STATUS_COLORS.map((color) => ({
            props: { color },
            style: {
              color: theme.palette[color].dark,
              backgroundColor: soft(theme.palette[color].main, 10),
              borderColor: soft(theme.palette[color].main, 24),
            },
          })),
        ],
      }),
      sizeSmall: { height: 24 },
      label: { paddingInline: 8 },
      clickable: ({ theme }) => ({ '&:hover': { backgroundColor: theme.palette.surface.sunken } }),
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      tooltip: ({ theme }) => ({ ...theme.typography.caption, backgroundColor: theme.palette.text.primary, padding: '6px 10px' }),
      arrow: ({ theme }) => ({ color: theme.palette.text.primary }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({ minHeight: 44, borderBottom: `1px solid ${theme.palette.divider}` }),
      indicator: { height: 2, borderRadius: 2 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 44,
        minWidth: 0,
        paddingInline: theme.spacing(1.5),
        marginRight: theme.spacing(1),
        fontWeight: 500,
        color: theme.palette.text.secondary,
        '&:hover': { color: theme.palette.text.primary },
        '&.Mui-selected': { color: theme.palette.primary.main },
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        '&:hover': { backgroundColor: theme.palette.surface.subtle },
        '&.Mui-selected': { backgroundColor: soft(theme.palette.primary.main, 7), color: theme.palette.primary.main },
        '&.Mui-selected:hover': { backgroundColor: soft(theme.palette.primary.main, 10) },
      }),
    },
  },
  MuiSkeleton: {
    styleOverrides: { root: ({ theme }) => ({ backgroundColor: theme.palette.surface.sunken }) },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({ height: 6, borderRadius: 3, backgroundColor: theme.palette.surface.sunken }),
      bar: { borderRadius: 3 },
    },
  },
};
